import React, { createRef, FunctionComponent, KeyboardEventHandler, MouseEventHandler, useContext } from "react";

import {
    AlignItems,
    BooleanSwitcher,
    BooleanSwitcherChildrenProps,
    FlexDirection,
    FlexGrow,
    Icon,
    IconColor,
    IconSize,
    JustifyContent,
    Layout,
    SpacingSize,
} from "@multiweb/reactComponents";
import { bemClassNames, TAB_INDEX_FOCUSABLE_ONLY, useContentOverflows, useSetFocus } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { NOTIFICATIONS_HEADER_HEIGHT } from "../../constants";
import { ScrollContext } from "../../context";
import { activationKeyBlockHandler, activationMouseBlockHandler, createActivationKeyHandler, createActivationMouseHandler, createMenuCustomScroll } from "../../utils";
import { createCreatedDateText } from "./utils";

export type NotificationItemComponentProps = {
    /** Barva pozadí ikony při nepřečtené notifikaci */
    activeColorHex: string;
    /** Časový údaj vytvoření */
    createdDate: Date;
    /** Kód ikony notifikace */
    iconCode: IconCode;
    /** Obsah zprávy notifikace */
    text?: string;
    /** Callback volaný po kliknutí na notifikaci. Reaguje na primární i ostatní tlačítka myši (přidává se na onClick i onAuxClick). */
    onClick: MouseEventHandler;
    /** Nadpis notifikace */
    title?: string;
    /** Příznak, zda je notifikace nepřečtená */
    isUnread?: boolean;
    /** Příznak, zda je komponenta zaměřená */
    isFocused: boolean;
    /** Callback volaný při uvolnění klávesy */
    onKeyUp?: KeyboardEventHandler;
    /** Kód jazyk aplikace */
    localeCode: string;
};

const classNames = bemClassNames("notification-item-component");

const TITLE_PLACEHOLDER = "—";

/** Komponenta slouží jako položka menu reprezentující notifikaci. */
export const NotificationItemComponent: FunctionComponent<NotificationItemComponentProps> = ({
    activeColorHex,
    createdDate,
    iconCode,
    isUnread = false,
    onClick,
    text,
    title,
    isFocused,
    onKeyUp,
    localeCode,
}) => {
    const titleRef = createRef<HTMLDivElement>();
    const componentRef = createRef<HTMLDivElement>();
    const contentOverflows = useContentOverflows<HTMLDivElement>(titleRef);
    const titleText = title || TITLE_PLACEHOLDER;
    const createdDateText = createCreatedDateText(createdDate, localeCode);
    const scroll = useContext(ScrollContext);
    useSetFocus<HTMLDivElement>(isFocused, componentRef, createMenuCustomScroll(scroll, NOTIFICATIONS_HEADER_HEIGHT));
    const handleClick = createActivationMouseHandler(onClick);
    const handleKeyUp = createActivationKeyHandler(onKeyUp);
    return (
        <BooleanSwitcher>
            {({ value: isHovered, turnOn: onMouseEnter, turnOff: onMouseLeave }: BooleanSwitcherChildrenProps) => (
                <div
                    className={classNames({ active: isHovered || isFocused })}
                    title={contentOverflows ? title : undefined}
                    ref={componentRef}
                    tabIndex={TAB_INDEX_FOCUSABLE_ONLY}
                    onClick={handleClick}
                    onAuxClick={handleClick}
                    onMouseDown={activationMouseBlockHandler}
                    onKeyUp={handleKeyUp}
                    onKeyDown={activationKeyBlockHandler}
                    {...{ onMouseEnter, onMouseLeave }}
                >
                    <Layout
                        spacingSizeVertical={SpacingSize.s}
                        spacingSizeHorizontal={SpacingSize.m}
                        flexGrow={FlexGrow.one}
                        collapse={true}
                    >
                        <Layout
                            flexDirection={FlexDirection.row}
                            justifyContent={JustifyContent.center}
                            alignItems={AlignItems.center}
                            flexGrow={FlexGrow.one}
                            collapse={true}
                        >
                            <Layout spacingSizeRight={SpacingSize.m}>
                                <div
                                    className={classNames("badge")}
                                    style={{ backgroundColor: isUnread ? activeColorHex : undefined }}
                                >
                                    <Icon
                                        color={IconColor.whiteFixed}
                                        size={IconSize.small}
                                        {...{ iconCode }}
                                    />
                                </div>
                            </Layout>
                            <Layout
                                flexDirection={FlexDirection.column}
                                justifyContent={JustifyContent.center}
                                flexGrow={FlexGrow.one}
                                collapse={true}
                            >
                                <Layout
                                    flexDirection={FlexDirection.row}
                                    justifyContent={JustifyContent.spaceBetween}
                                    collapse={true}
                                >
                                    <div
                                        className={classNames("title", { active: isUnread })}
                                        ref={titleRef}
                                    >
                                        {titleText}
                                    </div>
                                    <div className={classNames("timestamp")}>
                                        {createdDateText}
                                    </div>
                                </Layout>
                                <div className={classNames("text")}>
                                    {text}
                                </div>
                            </Layout>
                        </Layout>
                    </Layout>
                </div>
            )}
        </BooleanSwitcher>
    );
};
